import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { useEffect, useMemo } from 'react';
import FormHidden from 'components/common/form/layout/control/FormHidden';
import { Box } from '@mui/material';
import { IHistoryRecord } from 'app/app/editor/useHistory';

export interface FormAppBuilderProps extends FormControlProps {
    wrapWithContainer: boolean;
    isPortalPageTemplate: boolean; // True if this is a Portal Page Template
    withTemplate: boolean; // True if this is Portal Page with Template
}

const HistoryToSave = 10;

export default function FormAppBuilder({ controlProps }: FormElementControlPropsType<FormAppBuilderProps>) {
    const pageName = controlProps.form.hookFormWatch('internal_name');
    const isBuildInd = controlProps.form.hookFormWatch('is_builder_ind') == 'Y';

    const onJsonModified = (event: any) => {
        if (event?.data?.name == 'jsonModified') {
            controlProps.form.hookFormSetValue('javascript_code', event.data.data, { shouldDirty: true });
        }
        if (event?.data?.name == 'historyModified') {
            if (!Array.isArray(event.data.data)) {
                return;
            }

            let history: Array<IHistoryRecord> = [];
            if (event.data.data.length > HistoryToSave) {
                const s = event.data.data.length / HistoryToSave;
                history.push(event.data.data[0]);
                for (let i = 0; i < event.data.data.length; i += s) {
                    const j = Math.ceil(i);
                    if (j == 0 || j == event.data.data.length - 1) {
                        continue;
                    }
                    history.push(event.data.data[j]);
                }
                history.push(event.data.data[event.data.data.length - 1]);
            } else {
                history = event.data.data;
            }

            controlProps.form.hookFormSetValue('javascript_code_history', JSON.stringify(history));
        }
    };

    useEffect(() => {
        window.addEventListener('message', onJsonModified);
        return () => window.removeEventListener('message', onJsonModified);
    }, []);

    const src = useMemo(() => {
        if (!pageName) {
            return '';
        }
        return (
            `/${controlProps.isPortalPageTemplate ? 'pt' : 'p'}/${pageName}?isEditMode=Y&withTemplate=${controlProps.withTemplate ? 'Y' : 'N'}&pageSession=` +
            new Date().getTime()
        );
    }, [pageName, controlProps.isPortalPageTemplate, controlProps.withTemplate]);

    if (src == '' || !controlProps.form.formDidMount || !isBuildInd) {
        return null;
    }

    return (
        <>
            <FormHidden controlProps={controlProps}></FormHidden>
            <Box
                component={'iframe'}
                src={src}
                sx={{
                    border: '0',
                    mx: (theme) => theme.size.pxValue(theme.size.containerIndentX * -1),
                    alignSelf: 'stretch',
                    mt: '-32px !important',
                    mb: '-24px !important',
                    height: 'calc(100% + 56px)',
                    flexShrink: 0,
                }}
            />
        </>
    );
}

export class FormAppBuilderBuilder extends FormComponentBuilder {
    prepareProps(): FormAppBuilderProps {
        return {
            ...this.controlProps,
            isPortalPageTemplate: this.elementProps.componentProps?.isPortalPageTemplate ?? false,
            withTemplate: this.elementProps.componentProps?.withTemplate ?? false,
            wrapWithContainer: false,
        };
    }
}
